import React, { useRef, useState, useMemo, useCallback, useEffect } from 'react';
import { useFieldLayout } from '../../hooks/useFieldLayout';
import { getFormationPositions } from '../../utils/formations';
import { WebPlayerCard, PaymentStatus } from './WebPlayerCard';
import { Player, getMatchResultDisplay } from '../../types';

interface WebSoccerFieldProps {
  team1: (Player | null)[];
  team2: (Player | null)[];
  team1Name: string;
  team2Name: string;
  team1Color?: string;
  team2Color?: string;
  team1Goals?: number | null;
  team2Goals?: number | null;
  /**
   * Cómo cargó el resultado el organizador. Con 'difference' los goles son (dif, 0),
   * así que el marcador se muestra como "Ganó X por N goles" y no como "0 - 2".
   */
  scoreMode?: string | null;
  maxPlayers: number;
  isFinished?: boolean;
  formationIndex?: number;
  onPlayerClick?: (player: Player) => void;
  onEmptySlotClick?: (teamIndex: number, positionIndex: number) => void;
  playerStatsMap?: Record<string, { goals: number; assists: number; saves: number }>;
  mvpPlayerIds?: string[];
  reportStatusMap?: Record<string, { pending: boolean; approved: boolean; rejected: boolean; unsubmitted: boolean }>;
  paymentStatusMap?: Record<string, PaymentStatus>;
  currentUserId?: string;
  pricePerPlayer?: number;
  userIsCreator?: boolean;
  // Drag & drop
  onFieldDragStart?: (player: Player) => void;
  onFieldDragEnd?: () => void;
  onFieldDragOver?: (player: Player | null) => void;
  onFieldDrop?: (targetPlayer: Player | null, teamIndex?: number, positionIndex?: number) => Promise<void>;
  draggedPlayer?: Player | null;
  dragOverPlayer?: Player | null;
  // Substitutes panel
  substitutes?: Player[];
}

// SVG field markings for vertical (portrait) layout
function VerticalFieldSVG() {
  return (
    <svg viewBox="0 0 300 450" style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}>
      {/* Field background with grass stripes */}
      <defs>
        <pattern id="grassStripes" width="300" height="90" patternUnits="userSpaceOnUse">
          <rect x="0" y="0" width="300" height="45" fill="#1a5e2e" />
          <rect x="0" y="45" width="300" height="45" fill="#175528" />
        </pattern>
      </defs>
      <rect x="0" y="0" width="300" height="450" fill="url(#grassStripes)" />
      {/* Field border */}
      <rect x="10" y="10" width="280" height="430" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" rx="2" />
      {/* Center line */}
      <line x1="10" y1="225" x2="290" y2="225" stroke="rgba(255,255,255,0.2)" strokeWidth="1" />
      {/* Center circle */}
      <circle cx="150" cy="225" r="35" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="1" />
      <circle cx="150" cy="225" r="3" fill="rgba(255,255,255,0.3)" />
      {/* Top penalty area */}
      <rect x="75" y="10" width="150" height="55" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
      <rect x="110" y="10" width="80" height="22" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
      {/* Top penalty arc */}
      <path d="M 120 65 Q 150 45 180 65" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
      {/* Bottom penalty area */}
      <rect x="75" y="385" width="150" height="55" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
      <rect x="110" y="418" width="80" height="22" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
      {/* Bottom penalty arc */}
      <path d="M 120 385 Q 150 405 180 385" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
    </svg>
  );
}

// SVG field markings for horizontal (landscape) layout
function HorizontalFieldSVG() {
  return (
    <svg viewBox="0 0 560 380" preserveAspectRatio="none" style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}>
      <defs>
        <pattern id="grassStripesH" width="112" height="380" patternUnits="userSpaceOnUse">
          <rect x="0" y="0" width="56" height="380" fill="#1a5e2e" />
          <rect x="56" y="0" width="56" height="380" fill="#175528" />
        </pattern>
      </defs>
      <rect x="0" y="0" width="560" height="380" fill="url(#grassStripesH)" />
      <rect x="10" y="10" width="540" height="360" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" rx="2" />
      {/* Center line */}
      <line x1="280" y1="10" x2="280" y2="370" stroke="rgba(255,255,255,0.2)" strokeWidth="1" />
      {/* Center circle */}
      <circle cx="280" cy="190" r="45" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="1" />
      <circle cx="280" cy="190" r="3" fill="rgba(255,255,255,0.3)" />
      {/* Left penalty area */}
      <rect x="10" y="100" width="70" height="180" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
      <rect x="10" y="140" width="30" height="100" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
      {/* Left penalty arc */}
      <path d="M 80 155 Q 60 190 80 225" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
      {/* Right penalty area */}
      <rect x="480" y="100" width="70" height="180" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
      <rect x="520" y="140" width="30" height="100" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
      {/* Right penalty arc */}
      <path d="M 480 155 Q 500 190 480 225" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
    </svg>
  );
}

// Empty slot component for unfilled positions
const WebEmptySlot: React.FC<{
  role: string;
  size: 'sm' | 'md' | 'lg';
  onClick?: () => void;
  userIsCreator?: boolean;
}> = ({ role, size, onClick, userIsCreator }) => {
  const slotSizes = { sm: 36, md: 42, lg: 48 };
  const fontSizes = { sm: 14, md: 16, lg: 18 };
  const labelSizes = { sm: 7, md: 8, lg: 9 };
  const s = slotSizes[size];
  const [hovered, setHovered] = useState(false);

  const actionLabel = userIsCreator ? 'INVITAR' : 'LIBRE';

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={userIsCreator ? 'Click para invitar jugador' : 'Lugar disponible'}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.15s ease',
        transform: hovered && onClick ? 'scale(1.1)' : 'scale(1)',
      }}
    >
      <div style={{
        width: s, height: s, borderRadius: s / 2,
        border: hovered && onClick
          ? '2px dashed rgba(0,230,118,0.8)'
          : '2px dashed rgba(180,180,180,0.6)',
        background: hovered && onClick
          ? 'rgba(0,230,118,0.15)'
          : 'rgba(80,80,80,0.3)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        animation: 'slotPulse 2s ease-in-out infinite',
        transition: 'border-color 0.15s ease, background 0.15s ease',
      }}>
        <span style={{
          fontSize: fontSizes[size],
          color: hovered && onClick ? 'rgba(0,230,118,0.9)' : 'rgba(200,200,200,0.8)',
          fontWeight: 300,
          transition: 'color 0.15s ease',
        }}>+</span>
      </div>
      <span style={{
        fontSize: labelSizes[size],
        fontWeight: 700,
        color: '#FFD700',
        textShadow: '0 1px 2px rgba(0,0,0,0.8)',
      }}>
        {role}
      </span>
      <span style={{
        fontSize: 7,
        color: hovered && onClick ? 'rgba(0,230,118,0.9)' : 'rgba(200,200,200,0.6)',
        fontWeight: 600,
        transition: 'color 0.15s ease',
      }}>
        {actionLabel}
      </span>
    </div>
  );
};

export const WebSoccerField: React.FC<WebSoccerFieldProps> = ({
  team1,
  team2,
  team1Name,
  team2Name,
  team1Color = '#4CAF50',
  team2Color = '#2196F3',
  team1Goals,
  team2Goals,
  scoreMode,
  maxPlayers,
  isFinished = false,
  formationIndex = 0,
  onPlayerClick,
  onEmptySlotClick,
  playerStatsMap,
  mvpPlayerIds = [],
  reportStatusMap,
  paymentStatusMap,
  currentUserId,
  pricePerPlayer,
  userIsCreator = false,
  onFieldDragStart,
  onFieldDragEnd,
  onFieldDragOver,
  onFieldDrop,
  draggedPlayer,
  dragOverPlayer,
  substitutes = [],
}) => {
  const layout = useFieldLayout();
  const containerRef = useRef<HTMLDivElement>(null);
  const [initialAnimDone, setInitialAnimDone] = useState(false);

  // Mark initial entry animation as done after stagger completes
  useEffect(() => {
    const timer = setTimeout(() => setInitialAnimDone(true), 800);
    return () => clearTimeout(timer);
  }, []);

  // Payment summary for header
  const paymentSummary = useMemo(() => {
    if (!paymentStatusMap) return null;
    const entries = Object.values(paymentStatusMap);
    const paid = entries.filter(s => s === 'confirmed').length;
    const total = entries.length;
    const unpaid = total - paid;
    return { paid, total, unpaid };
  }, [paymentStatusMap]);

  // Clicking a player (field or substitute) always opens the full PlayerActionsModal,
  // for every viewer — organizer included. Drag & drop is handled separately via
  // native HTML5 drag events, which don't trigger onClick.
  const handlePlayerClick = useCallback((player: Player) => {
    onPlayerClick?.(player);
  }, [onPlayerClick]);

  const team1Positions = getFormationPositions(
    maxPlayers, formationIndex, false, layout.isVertical
  );
  const team2Positions = getFormationPositions(
    maxPlayers, formationIndex, true, layout.isVertical
  );

  const renderPlayer = (
    player: Player,
    index: number,
    positions: { x: number; y: number; role: string }[],
    teamColor: string,
    teamIndex: number
  ) => {
    const pos = positions[index];
    if (!pos) return null;

    const stats = playerStatsMap?.[player.id];
    const isMVP = mvpPlayerIds.includes(player.id);
    const reportStatus = reportStatusMap?.[player.id];
    const paymentStatus = paymentStatusMap?.[player.id];
    const isCurrentUser = !!currentUserId && player.userId === currentUserId;

    const left = `${pos.x * 100}%`;
    const top = `${pos.y * 100}%`;
    const globalIndex = teamIndex * maxPlayers + index;
    const isBeingDragged = draggedPlayer?.id === player.id;
    const isDragTarget = dragOverPlayer?.id === player.id;
    const canDrag = userIsCreator && !!onFieldDragStart;

    return (
      <div
        key={player.id}
        data-testid={`field-player-${player.id}`}
        data-dragging={isBeingDragged ? 'true' : undefined}
        draggable={canDrag}
        onDragStart={canDrag ? (e) => {
          e.dataTransfer.setData('text/plain', player.id);
          e.dataTransfer.effectAllowed = 'move';
          // Hide native drag image
          const ghost = document.createElement('div');
          ghost.style.cssText = 'position:absolute;top:-1000px;width:1px;height:1px;opacity:0';
          document.body.appendChild(ghost);
          e.dataTransfer.setDragImage(ghost, 0, 0);
          setTimeout(() => ghost.remove(), 0);
          onFieldDragStart(player);
        } : undefined}
        onDragEnd={canDrag ? () => onFieldDragEnd?.() : undefined}
        onDragOver={canDrag ? (e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          onFieldDragOver?.(player);
        } : undefined}
        onDrop={canDrag ? (e) => {
          e.preventDefault();
          if (draggedPlayer && draggedPlayer.id !== player.id) {
            onFieldDrop?.(player, teamIndex, index);
          }
        } : undefined}
        style={{
          position: 'absolute',
          left,
          top,
          transform: `translate(-50%, -50%)${isDragTarget ? ' scale(1.08)' : ''}`,
          zIndex: isCurrentUser ? 12 : 10,
          animation: initialAnimDone ? 'none' : 'fadeSlideIn 0.3s ease forwards',
          animationDelay: initialAnimDone ? undefined : `${globalIndex * 0.05}s`,
          opacity: initialAnimDone ? 1 : 0,
          padding: layout.mode === 'mobile' ? 8 : 4,
          margin: layout.mode === 'mobile' ? -8 : -4,
          cursor: canDrag ? 'grab' : undefined,
          transition: isDragTarget ? 'transform 0.15s ease' : undefined,
          outline: isDragTarget ? '2px solid #FFD700' : undefined,
          outlineOffset: 2,
          borderRadius: 8,
        }}
      >
        <WebPlayerCard
          name={player.name}
          position={player.position}
          rating={player.rating}
          teamColor={teamColor}
          size={layout.cardSize}
          nameMaxChars={layout.nameMaxChars}
          avatar={player.user?.avatar}
          isGuest={!player.userId}
          goals={stats?.goals}
          assists={stats?.assists}
          saves={stats?.saves}
          isFinished={isFinished}
          isMVP={isMVP}
          isCurrentUser={isCurrentUser}
          onClick={() => handlePlayerClick(player)}
          hasPendingReport={reportStatus?.pending}
          hasApprovedReport={reportStatus?.approved}
          hasRejectedReport={reportStatus?.rejected}
          hasUnsubmittedReport={reportStatus?.unsubmitted}
          paymentStatus={paymentStatus}
        />
      </div>
    );
  };

  const renderEmptySlot = (
    index: number,
    positions: { x: number; y: number; role: string }[],
    teamIndex: number
  ) => {
    const pos = positions[index];
    if (!pos) return null;

    const left = `${pos.x * 100}%`;
    const top = `${pos.y * 100}%`;
    const canDrop = userIsCreator && !!draggedPlayer;

    return (
      <div
        key={`empty-${teamIndex}-${index}`}
        data-testid={`field-empty-slot-${teamIndex}-${index}`}
        onDragOver={canDrop ? (e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
        } : undefined}
        onDrop={canDrop ? (e) => {
          e.preventDefault();
          onFieldDrop?.(null, teamIndex, index);
        } : undefined}
        style={{
          position: 'absolute',
          left,
          top,
          transform: 'translate(-50%, -50%)',
          zIndex: 10,
        }}
      >
        <WebEmptySlot
          role={pos.role}
          size={layout.cardSize}
          onClick={onEmptySlotClick ? () => onEmptySlotClick(teamIndex, index) : undefined}
          userIsCreator={userIsCreator}
        />
      </div>
    );
  };

  // Render all slots (players + empty) for a team
  const renderTeamSlots = (
    teamPlayers: (Player | null)[],
    positions: { x: number; y: number; role: string }[],
    teamColor: string,
    teamIndex: number
  ) => {
    const slots: React.ReactNode[] = [];
    for (let i = 0; i < maxPlayers; i++) {
      const player = teamPlayers[i];
      if (player) {
        slots.push(renderPlayer(player, i, positions, teamColor, teamIndex));
      } else {
        slots.push(renderEmptySlot(i, positions, teamIndex));
      }
    }
    return slots;
  };

  return (
    <div data-testid="web-soccer-field-wrapper">
      {/* Payment summary header */}
      {paymentSummary && pricePerPlayer != null && pricePerPlayer > 0 && (
        <div
          data-testid="field-payment-header"
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '6px 12px', marginBottom: 4,
            background: 'rgba(0,0,0,0.4)', borderRadius: 8,
            fontSize: 11, fontWeight: 600,
          }}
        >
          <span data-testid="field-payment-paid" style={{ color: '#4CAF50' }}>
            Pagaron: {paymentSummary.paid}/{paymentSummary.total}
          </span>
          {paymentSummary.unpaid > 0 && (
            <span data-testid="field-payment-debt" style={{ color: '#F59E0B' }}>
              Deuda: ${(paymentSummary.unpaid * pricePerPlayer).toLocaleString('es-AR')}
            </span>
          )}
        </div>
      )}

      {/* Scoreboard header (finished matches) */}
      {isFinished && team1Goals != null && team2Goals != null && (() => {
        const t1Wins = team1Goals > team2Goals;
        const t2Wins = team2Goals > team1Goals;
        const draw = team1Goals === team2Goals;
        const result = getMatchResultDisplay({ team1Name, team2Name, team1Goals, team2Goals, scoreMode });
        const pillStyle: React.CSSProperties = {
          background: 'rgba(10,14,20,0.78)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          borderRadius: 12,
          padding: '6px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          border: '1px solid rgba(255,255,255,0.1)',
          fontFamily: 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        };

        // Cargado "por diferencia": no hay marcador real, solo quién ganó y por cuánto.
        if (result.kind === 'difference' || result.kind === 'draw') {
          const winnerColor = result.winner === 1 ? team1Color : result.winner === 2 ? team2Color : '#fff';
          return (
            <div
              data-testid="field-scoreboard"
              data-score-mode="difference"
              style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}
            >
              <div style={{ ...pillStyle, flexDirection: 'column', gap: 4, padding: '8px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 9, fontWeight: 700, letterSpacing: 1 }}>
                  <span style={{ color: team1Color, opacity: result.winner === 1 ? 1 : 0.75 }}>{team1Name.toUpperCase()}</span>
                  <span style={{ color: 'rgba(255,255,255,0.45)' }}>VS</span>
                  <span style={{ color: team2Color, opacity: result.winner === 2 ? 1 : 0.75 }}>{team2Name.toUpperCase()}</span>
                </div>
                <div
                  data-testid="field-scoreboard-result"
                  style={{ fontSize: 16, fontWeight: 800, color: winnerColor, lineHeight: '22px', textAlign: 'center' }}
                >
                  {result.label}
                </div>
              </div>
            </div>
          );
        }

        return (
          <div
            data-testid="field-scoreboard"
            data-score-mode="exact"
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: 8,
            }}
          >
            <div style={pillStyle}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  minWidth: 88,
                }}
              >
                <div style={{ fontSize: 9, fontWeight: 700, color: team1Color, opacity: t1Wins ? 1 : 0.75, letterSpacing: 1 }}>
                  {team1Name.toUpperCase()}
                </div>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 24,
                    height: 34,
                    fontSize: 30,
                    lineHeight: '34px',
                    fontWeight: 800,
                    color: team1Color,
                    opacity: t1Wins || draw ? 1 : 0.72,
                    fontVariantNumeric: 'tabular-nums',
                    fontFeatureSettings: '"tnum" 1, "lnum" 1',
                  }}
                >
                  {team1Goals}
                </span>
              </div>
              <div style={{ minWidth: 26, textAlign: 'center' }}>
                <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.45)' }}>-</span>
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  minWidth: 88,
                }}
              >
                <div style={{ fontSize: 9, fontWeight: 700, color: team2Color, opacity: t2Wins ? 1 : 0.75, letterSpacing: 1 }}>
                  {team2Name.toUpperCase()}
                </div>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 24,
                    height: 34,
                    fontSize: 30,
                    lineHeight: '34px',
                    fontWeight: 800,
                    color: team2Color,
                    opacity: t2Wins || draw ? 1 : 0.72,
                    fontVariantNumeric: 'tabular-nums',
                    fontFeatureSettings: '"tnum" 1, "lnum" 1',
                  }}
                >
                  {team2Goals}
                </span>
              </div>
            </div>
          </div>
        );
      })()}

      <div
        ref={containerRef}
        data-testid="web-soccer-field"
        style={{
          position: 'relative',
          width: '100%',
          height: layout.isVertical ? undefined : 'calc(100vh - 280px)',
          minHeight: layout.isVertical ? undefined : 200,
          aspectRatio: layout.isVertical ? '300 / 450' : undefined,
          overflow: 'hidden',
          borderRadius: layout.mode === 'mobile' ? 0 : 8,
        }}
      >
        {/* Injected keyframes for animations */}
        <style>{`
          @keyframes fadeSlideIn {
            from { opacity: 0; transform: translate(-50%, -40%); }
            to { opacity: 1; transform: translate(-50%, -50%); }
          }
          @keyframes slotPulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(180,180,180,0); }
            50% { box-shadow: 0 0 0 4px rgba(180,180,180,0.3); }
          }
          [data-testid^="field-player-"][data-dragging="true"] {
            opacity: 0.4 !important;
          }
        `}</style>

        {/* SVG Field */}
        {layout.isVertical ? <VerticalFieldSVG /> : <HorizontalFieldSVG />}

        {/* Team labels — dark pill with team-color dot, readable over any field/team color */}
        <div
          data-testid="field-team-label-1"
          style={{
            position: 'absolute',
            top: 14,
            left: layout.isVertical ? 14 : '15%',
            maxWidth: layout.isVertical ? '38%' : '30%',
            zIndex: 5,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            background: 'rgba(10,14,20,0.72)',
            borderRadius: 20,
            padding: '4px 10px 4px 8px',
            pointerEvents: 'none',
          }}
        >
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: team1Color, flexShrink: 0, boxShadow: '0 0 0 1.5px rgba(255,255,255,0.75)' }} />
          <span style={{ fontSize: 12, fontWeight: 700, color: '#fff', letterSpacing: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
            {team1Name.toUpperCase()}
          </span>
        </div>
        <div
          data-testid="field-team-label-2"
          style={{
            position: 'absolute',
            bottom: layout.isVertical ? 14 : undefined,
            top: layout.isVertical ? undefined : 14,
            right: layout.isVertical ? 14 : '15%',
            maxWidth: layout.isVertical ? '38%' : '30%',
            zIndex: 5,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            background: 'rgba(10,14,20,0.72)',
            borderRadius: 20,
            padding: '4px 8px 4px 10px',
            pointerEvents: 'none',
          }}
        >
          <span style={{ fontSize: 12, fontWeight: 700, color: '#fff', letterSpacing: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
            {team2Name.toUpperCase()}
          </span>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: team2Color, flexShrink: 0, boxShadow: '0 0 0 1.5px rgba(255,255,255,0.75)' }} />
        </div>

      {/* Team 1 slots (players + empty) */}
      {renderTeamSlots(team1, team1Positions, team1Color, 0)}

      {/* Team 2 slots (players + empty) */}
      {renderTeamSlots(team2, team2Positions, team2Color, 1)}
      </div>

      {/* Substitutes panel */}
      {substitutes.length > 0 && (
        <div
          data-testid="field-substitutes-panel"
          onDragOver={userIsCreator && draggedPlayer && !draggedPlayer.isSubstitute ? (e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            onFieldDragOver?.(null);
          } : undefined}
          onDrop={userIsCreator && draggedPlayer && !draggedPlayer.isSubstitute ? (e) => {
            e.preventDefault();
            onFieldDrop?.(null);
          } : undefined}
          style={{
            display: 'flex',
            gap: 12,
            padding: '8px 12px',
            alignItems: 'center',
            overflowX: 'auto',
            position: 'relative',
            background: dragOverPlayer === null && draggedPlayer && !draggedPlayer.isSubstitute
              ? 'rgba(255,215,0,0.15)'
              : 'rgba(0,0,0,0.3)',
            borderRadius: '0 0 8px 8px',
            borderTop: '1px solid rgba(255,255,255,0.08)',
            transition: 'background 0.2s ease',
          }}
        >
          <span
            data-testid="field-substitutes-label"
            style={{
              fontSize: 10,
              color: '#ccc',
              fontWeight: 700,
              alignSelf: 'center',
              whiteSpace: 'nowrap',
              letterSpacing: 1,
            }}
          >
            SUPLENTES ({substitutes.length})
          </span>
          {substitutes.map(player => {
            const isBeingDragged = draggedPlayer?.id === player.id;
            const canDrag = userIsCreator && !!onFieldDragStart;
            const paymentStatus = paymentStatusMap?.[player.id];
            return (
              <div
                key={player.id}
                data-testid={`field-substitute-${player.id}`}
                data-dragging={isBeingDragged ? 'true' : undefined}
                draggable={canDrag}
                onDragStart={canDrag ? (e) => {
                  e.dataTransfer.setData('text/plain', player.id);
                  e.dataTransfer.effectAllowed = 'move';
                  const ghost = document.createElement('div');
                  ghost.style.cssText = 'position:absolute;top:-1000px;width:1px;height:1px;opacity:0';
                  document.body.appendChild(ghost);
                  e.dataTransfer.setDragImage(ghost, 0, 0);
                  setTimeout(() => ghost.remove(), 0);
                  onFieldDragStart({ ...player, isSubstitute: true });
                } : undefined}
                onDragEnd={canDrag ? () => onFieldDragEnd?.() : undefined}
                onClick={() => handlePlayerClick(player)}
                style={{
                  flexShrink: 0,
                  cursor: canDrag ? 'grab' : 'pointer',
                  opacity: isBeingDragged ? 0.4 : 1,
                  transition: 'opacity 0.15s ease',
                }}
              >
                <WebPlayerCard
                  name={player.name}
                  position={player.position}
                  rating={player.rating}
                  teamColor="#888"
                  size={layout.mode === 'mobile' ? 'sm' : 'md'}
                  nameMaxChars={layout.nameMaxChars}
                  avatar={player.user?.avatar}
                  isGuest={!player.userId}
                  paymentStatus={paymentStatus === 'confirmed' ? paymentStatus : undefined}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
